// Import Libraries
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

// Import JS Modules
import { builderDispatch, WebUI, PrismUI } from '../../_utils/ui';
import getImageSources from '../image-sources';
import notifications from '../utils/notifications';
import LoaderAdditionalOptions from '../additional-options';
import ImageUploadFileValidator from '../../_utils/image-upload-file-validator';

const LoaderBase = {
  getChildContext() {
    const { additionalOptions = {} } = this.state;

    return {
      additionalOptions,
    };
  },

  componentDidMount() {
    window.addEventListener('orientationchange', this.handleOrientationChanged);
    window.addEventListener('keydown', this.handleKeyDown);

    this._isMounted = true;

    this.builderDispatchToken = builderDispatch.register(payload => {
      window.requestAnimationFrame(() => {
        switch (payload.type) {
          case PrismUI.ADD_PHOTOS:
            this.begin(
              payload.upload_url,
              payload.excludeServices,
              payload.callToActionButtonText,
              payload.callToActionButtonEvent,
              payload.additionalOptions
            );
            break;

          case WebUI.UPLOAD_SHOW_FAILURES:
            this.errorMessage = payload.message;

            if (payload.number_of_errors) {
              this.number_of_errors =
                this.number_of_errors + payload.number_of_errors;
            }

            this.showFailures();
            break;

          case PrismUI.CLOSE_ADD_PHOTOS_MODAL:
            this.close();
            break;

          case PrismUI.CLOUD_SERVICE_HEADER:
            this.setState({ serviceHeaderMounted: payload.mounted });
            break;

          case WebUI.UPLOAD_ERROR:
          case WebUI.UPLOAD_BEGUN:
          case PrismUI.IMAGES_UPDATED:
            this.setState({ loading: false });
            break;
        }
      });
    });

    $(window)
      .on('offline', this.handleGoneOffline)
      .on('online', this.handleGoneOnline);

    $(ReactDOM.findDOMNode(this))
      .modal({
        show: false,
      })
      .on('hide.bs.modal', e => {
        const { showUploadErrorsModal, showFailures } = this.state;

        if (showUploadErrorsModal) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.toggleUploadErrorsModal();

          return false;
        }

        this.setState({ focus: false, uploadErrors: [] });

        if (showFailures) {
          this.handleAbort();
        }
      })
      .on('hidden.bs.modal', () => {
        builderDispatch.dispatch({
          type: WebUI.UPLOAD_CLOSED,
        });
      });

    $(ReactDOM.findDOMNode(this)).on('shown.bs.modal', () => {
      this.setState({ focus: true });
    });
  },

  componentWillUnmount() {
    this._isMounted = false;
    builderDispatch.unregister(this.builderDispatchToken);

    $(window)
      .off('offline', this.handleGoneOffline)
      .off('online', this.handleGoneOnline);

    window.removeEventListener(
      'orientationchange',
      this.handleOrientationChanged
    );
  },

  open() {
    $(ReactDOM.findDOMNode(this)).modal('show');
  },

  close() {
    if (!this._isMounted) return;

    this.setState(() => ({ uploadErrors: [] }));

    $(ReactDOM.findDOMNode(this)).modal('hide');
  },

  begin(
    url,
    excludeServices = [],
    callToActionButtonText,
    callToActionButtonEvent,
    additionalOptions = {}
  ) {
    const { loading } = this.state;
    if (!App.order.storage_provider_present) {
      excludeServices.push('albums');
    }

    if (loading) return this.close();

    this.clearAll();
    this.setState(
      ({ target_album: prevTargetAlbum }) => {
        const extraFileTypes =
          additionalOptions[LoaderAdditionalOptions.EXTRA_FILE_TYPES] || [];
        return {
          upload_url: url,
          excludeServices: [].concat(excludeServices),
          callToActionButtonText,
          callToActionButtonEvent,
          additionalOptions,
          hideMenu: this.state.serviceHeaderMounted,
          uploadSource: additionalOptions.uploadSource,
          target_album:
            additionalOptions[LoaderAdditionalOptions.TARGET_ALBUM] ||
            prevTargetAlbum,
          fileUploadValidator: new ImageUploadFileValidator({
            extraFileTypes: extraFileTypes,
          }),
        };
      },
      () => {
        this.open();
      }
    );
  },

  handleSourceSelect(source_index, service) {
    this.setState({ source: source_index });
    notifications.service(service);
  },

  previews() {
    const imageSources = getImageSources();
    return imageSources.reduce((memo, source) => {
      const implementation = this.refs[source.service];
      if (implementation) {
        memo = memo.concat(implementation.filesSelected());
      }
      return memo;
    }, []);
  },

  handleFilesSelected() {
    const {
      additionalOptions = {},
      source: sourceIdx,
    } = this.state;

    const imageSources = getImageSources();
    const source = imageSources[sourceIdx]?.component,
          sourceAutoUploads = source?.prototype?.[LoaderAdditionalOptions.CLOSE_ON_FILE_SELECTION],
          pageAutoUploads = additionalOptions[LoaderAdditionalOptions.CLOSE_ON_FILE_SELECTION];

    if (pageAutoUploads || sourceAutoUploads) {
      this.handleAddImages();
    }

    this.forceUpdate();
  },

  handleImagePreviewError(file) {
    if (!this.state.uploadErrors.includes(file)) {
      this.setState(prevState => ({
        uploadErrors: [...prevState.uploadErrors, file],
      }));
    }
  },

  toggleUploadErrorsModal() {
    this.setState(prevState => ({
      showUploadErrorsModal: !prevState.showUploadErrorsModal,
    }));
  },

  removeAllUploadErrorsClick() {
    this.filterBrokenImages();

    this.setState(() => ({ uploadErrors: [], showUploadErrorsModal: false }));
  },

  handleRemovePreview(file) {
    builderDispatch.dispatch({
      type: WebUI.UPLOAD_PHOTO_REMOVED_FROM_TRAY,
      key: file.originalKey || file.url,
      service: file.service,
    });

    const implementation = this.refs[file.service];

    if (implementation) {
      implementation.removeFile(file);
    }

    if (this.state.uploadErrors) {
      this.setState(prevState => {
        const result = prevState.uploadErrors.filter(error => error !== file);
        return { uploadErrors: result };
      });
    }

    this.forceUpdate();
  },

  totalNumberOfFilesToUpload() {
    const imageSources = getImageSources();
    return imageSources.reduce((memo, source) => {
      const implementation = this.refs[source.service];
      if (!!implementation && implementation.files) {
        memo += implementation.totalNumberOfFilesSelected();
      }
      return memo;
    }, 0);
  },

  performUpload(upload_url = this.state.upload_url, additionalData) {
    this.close();

    // needs to change to CTABE when defined.
    notifications.clickEvent(this.state.callToActionButtonEvent);

    this.uploaded_index = 0;
    this.number_of_errors = 0;

    if (!this.totalNumberOfFilesToUpload()) {
      return;
    }

    builderDispatch.delayedDispatch({
      type: WebUI.UPLOAD_BEGUN,
      number_of_files: this.totalNumberOfFilesToUpload(),
      ...additionalData,
    });

    const imageSources = getImageSources();
    imageSources.forEach((source) => {
      const implementation = this.refs[source.service];
      if (implementation) {
        implementation.performUpload(
          upload_url,
          additionalData,
          this.handleFileUploaded,
          this.handleFileUploadError,
        );
      }
    });
  },

  handleFileUploaded(data, source) {
    const totalNumberOfFilesToUpload = this.totalNumberOfFilesToUpload();

    builderDispatch.dispatch({
      type: WebUI.UPLOAD_PROGRESS,
      data,
      source,
      number_of_files: totalNumberOfFilesToUpload,
    });

    if (++this.uploaded_index === totalNumberOfFilesToUpload) {
      builderDispatch.dispatch({
        type: WebUI.UPLOAD_DONE,
        number_of_files: totalNumberOfFilesToUpload,
        number_of_errors: this.number_of_errors,
      });
    }
  },

  handleFileUploadError(message) {
    const totalNumberOfFilesToUpload = this.totalNumberOfFilesToUpload();

    builderDispatch.dispatch({
      type: WebUI.UPLOAD_ERROR,
      number_of_errors: ++this.number_of_errors,
    });

    if (++this.uploaded_index === totalNumberOfFilesToUpload) {
      builderDispatch.dispatch({
        type: WebUI.UPLOAD_DONE,
        number_of_files: totalNumberOfFilesToUpload,
        number_of_errors: this.number_of_errors,
        message: message || '',
      });
    }
  },

  handleGoneOffline() {
    const imageSources = getImageSources();
    imageSources.forEach(source => {
      const implementation = this.refs[source.service];
      if (implementation) {
        implementation.pause();
      }
    });

    builderDispatch.dispatch({
      type: WebUI.UPLOAD_PAUSED,
    });
  },

  handleGoneOnline() {
    const imageSources = getImageSources();
    imageSources.forEach(source => {
      const implementation = this.refs[source.service];
      if (implementation) {
        implementation.restart();
      }
    });

    builderDispatch.dispatch({
      type: WebUI.UPLOAD_RESUMED,
    });
  },

  handleAbort() {
    this.setState(
      {
        uploadErrors: [],
        showFailures: false,
      },
      () => {
        this.close();

        window.requestAnimationFrame(() => {
          builderDispatch.dispatch({
            type: WebUI.UPLOAD_ABORTED,
          });
        });

        this.clearAll();
      }
    );
  },

  handleOrientationChanged() {
    setTimeout(this.setWindowSize, 100);
  },

  showFailures() {
    const imageSources = getImageSources();
    imageSources.forEach(source => {
      const implementation = this.refs[source.service];
      if (implementation) {
        implementation.clearUploaded();
      }
    });

    this.setState(
      {
        showFailures: true,
      },
      () => {
        this.open();
      }
    );
  },

  clearAll() {
    const imageSources = getImageSources();
    imageSources.forEach(source => {
      const implementation = this.refs[source.service];
      if (implementation) {
        implementation.clear();
      }
    });
  },

  handleTargetAlbumSelected(album) {
    this.setState({
      target_album: album,
    });
  },

  getWindowSize() {
    return {
      windowHeight: window.innerHeight,
      windowWidth: window.innerWidth,
    };
  },

  setWindowSize() {
    this.setState(this.getWindowSize);
  },

  filterBrokenImages() {
    const { uploadErrors } = this.state;

    const imageSources = getImageSources();
    imageSources.reduce((memo, source) => {
      const implementation = this.refs[source.service];
      if (
        implementation &&
        !!implementation.files &&
        implementation.files.length > 0
      ) {
        implementation.files = implementation.files.filter(
          file => !uploadErrors.includes(file)
        );
      }
      return memo;
    }, []);
  },
};

LoaderBase.childContextTypes = {
  additionalOptions: PropTypes.object,
};

export default LoaderBase;
